import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  listDeveloperKeys,
  createDeveloperKey,
  revokeDeveloperKey,
  getDeveloperSubscription,
  subscribeDeveloperApi,
  publicDocsUrl,
} from '../lib/developerApi'

export default function DeveloperPortalPage() {
  const [keys, setKeys] = useState<Array<Record<string, unknown>>>([])
  const [sub, setSub] = useState<{ highestTier?: string; keys?: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('Production')
  const [justCreated, setJustCreated] = useState<string | null>(null)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try {
      const [k, s] = await Promise.all([listDeveloperKeys(), getDeveloperSubscription()])
      setKeys(k.keys || [])
      setSub(s)
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Sign in required')
      setKeys([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onCreate = async () => {
    setErr('')
    setJustCreated(null)
    try {
      const res = await createDeveloperKey(newName || 'API Key')
      setJustCreated(res.key as string)
      await load()
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Could not create key')
    }
  }

  const onRevoke = async (id: string) => {
    if (!confirm('Revoke this API key?')) return
    setErr('')
    try {
      await revokeDeveloperKey(id)
      await load()
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Revoke failed')
    }
  }

  const onSubscribe = async (tier: 'pro' | 'enterprise') => {
    setErr('')
    try {
      const { url } = await subscribeDeveloperApi(tier, 'monthly')
      if (url) window.location.href = url
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Checkout unavailable — set Stripe price env vars on the backend.'
      )
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <img src="/favicon.png" alt="" className="h-8 w-8" />
            <span className="font-semibold">DIZEE Developer API</span>
          </div>
          <div className="flex gap-4 text-sm">
            <Link to="/docs" className="text-indigo-600 hover:text-indigo-800">
              Documentation
            </Link>
            <a href={publicDocsUrl()} className="text-gray-600 hover:text-gray-900" target="_blank" rel="noreferrer">
              OpenAPI (Swagger)
            </a>
            <Link to="/home" className="text-gray-600 hover:text-gray-900">
              Home
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl font-semibold tracking-tight">Build on DIZEE</h1>
        <p className="mt-2 max-w-2xl text-gray-600">
          Use API keys to access your shows, financials, team, and activity programmatically. Higher tiers unlock write
          access and platform-wide data.
        </p>

        {err && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {err}
          </div>
        )}

        <section className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { tier: 'Free', desc: '100 req/day, read-only, 1 key', price: '$0' },
            { tier: 'Pro', desc: '10k req/day, read+write, 5 keys', price: 'Stripe' },
            { tier: 'Enterprise', desc: '100k req/day, platform discovery', price: 'Stripe' },
          ].map((card) => (
            <div key={card.tier} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{card.tier}</p>
              <p className="mt-2 text-2xl font-semibold">{card.price}</p>
              <p className="mt-2 text-sm text-gray-600">{card.desc}</p>
              {card.tier === 'Free' && (
                <p className="mt-4 text-xs text-gray-500">Create a key below after sign-in.</p>
              )}
              {card.tier === 'Pro' && (
                <button
                  type="button"
                  onClick={() => onSubscribe('pro')}
                  className="mt-4 w-full rounded-lg bg-gray-900 py-2 text-sm font-medium text-white hover:bg-gray-800"
                >
                  Upgrade to Pro
                </button>
              )}
              {card.tier === 'Enterprise' && (
                <button
                  type="button"
                  onClick={() => onSubscribe('enterprise')}
                  className="mt-4 w-full rounded-lg border border-gray-300 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  Upgrade to Enterprise
                </button>
              )}
            </div>
          ))}
        </section>

        {sub && (
          <p className="mt-8 text-sm text-gray-600">
            Current highest tier: <strong className="text-gray-900">{sub.highestTier}</strong> · Active keys:{' '}
            {sub.keys}
          </p>
        )}

        <section className="mt-10 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Your API keys</h2>
          <p className="mt-1 text-sm text-gray-500">Sign in to the app to create and manage keys.</p>

          {justCreated && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
              <p className="font-medium text-emerald-900">Copy your key now — it won’t be shown again:</p>
              <code className="mt-2 block break-all rounded bg-white p-2 text-xs">{justCreated}</code>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Key label"
              className="min-w-[200px] flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={onCreate}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Create key
            </button>
          </div>

          {loading ? (
            <p className="mt-6 text-sm text-gray-500">Loading…</p>
          ) : keys.length === 0 ? (
            <p className="mt-6 text-sm text-gray-500">No keys yet.</p>
          ) : (
            <ul className="mt-6 divide-y divide-gray-100">
              {keys.map((k) => (
                <li key={String(k._id)} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                  <div>
                    <span className="font-medium">{String(k.name)}</span>
                    <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{String(k.tier)}</span>
                    <p className="text-xs text-gray-500">
                      {String(k.keyPrefix)}… · {String(k.status)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRevoke(String(k._id))}
                    className="text-red-600 hover:text-red-800"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  )
}
